import { HTTP_STATUS, isApiError, isErrorEnvelopeBody, isNetworkError } from '@brightlocal/api-client';

import { isNetworkApiError } from 'shared/utils';

import { isReportCreateError } from './reportCreateError';

/** Why a report creation failed, as far as the response tells us. */
export const REPORT_CREATE_ERROR_KINDS = {
  /** The plan or its credits do not allow the report; expected for lapsed accounts. */
  ENTITLEMENT: 'entitlement',
  /** A report already exists or is already running for the location: a 409, or a 400 saying so. */
  DUPLICATE: 'duplicate',
  /** The location has no review site an RM report can collect from. */
  NO_SOURCE: 'no_source',
  /** The backend, or the app itself, rejected the data (e.g. location country vs GBP coordinates). */
  VALIDATION: 'validation',
  /** Edge / gateway failure: time-outs, bad gateway, rate limiting. */
  GATEWAY: 'gateway',
  UNKNOWN: 'unknown'
} as const;

/** Union of the report-creation error kinds. */
export type ReportCreateErrorKind = (typeof REPORT_CREATE_ERROR_KINDS)[keyof typeof REPORT_CREATE_ERROR_KINDS];

// 400 body keys the backend uses for plan and credit rejections
const ENTITLEMENT_BODY_KEYS = new Set(['noCredits', 'reportRun', 'generalSubscriptionIssue', 'searchEngines']);

// Manage API error codes for a plan that does not include the tool or has no runs left
const ENTITLEMENT_ERROR_CODES = new Set(['RM_NOT_AVAILABLE', 'RM_ACCESS_DENIED', 'REPORT_RUN_QUOTA_EXCEEDED']);

// RM answers this when no directory is left for the report to collect from
const NO_SOURCE_ERROR_CODE = 'NO_DIRECTORY_INCLUDED';

// 400 body keys for a report that is already running for the location
const DUPLICATE_BODY_KEYS = new Set(['reportRunning']);

// "There are multiple X reports for the selected location" / "Multiple reports for selected location."
const DUPLICATE_MESSAGE = /multiple[\w\s]*reports?/i;

const GATEWAY_STATUS_RANGES = [
  [502, 504],
  [520, 527]
] as const;

// Cloudflare / nginx edge statuses and rate limiting
const isGatewayStatus = (status: number) =>
  status === HTTP_STATUS.TOO_MANY_REQUESTS ||
  GATEWAY_STATUS_RANGES.some(([from, to]) => status >= from && status <= to);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * First key and value of an API error body; a top-level `errors` wrapper is unwrapped.
 * A Manage API error envelope gives its machine code and message instead, taken from its first
 * detail when it has one, because a validation error names the failing rule only there.
 */
const getBodyEntry = (body: unknown) => {
  if (isErrorEnvelopeBody(body)) {
    const [detail] = body.details ?? [];
    const { code, message } = detail ?? body;

    return { key: code, value: message };
  }
  if (!isRecord(body)) {
    return null;
  }

  const inner = isRecord(body.errors) ? body.errors : body;
  const [entry] = Object.entries(inner);

  return entry ? { key: entry[0], value: entry[1] } : null;
};

const describeValue = (value: unknown) => (typeof value === 'string' ? value : JSON.stringify(value));

interface ResolveKindParams {
  httpStatus: number | undefined;
  apiErrorKey: string | undefined;
  apiErrorDetail: string | undefined;
  isAppDetected: boolean;
}

const resolveKind = ({ httpStatus, apiErrorKey, apiErrorDetail, isAppDetected }: ResolveKindParams) => {
  if (isAppDetected) {
    return REPORT_CREATE_ERROR_KINDS.VALIDATION;
  }
  if (httpStatus === undefined) {
    return REPORT_CREATE_ERROR_KINDS.UNKNOWN;
  }
  if (isGatewayStatus(httpStatus)) {
    return REPORT_CREATE_ERROR_KINDS.GATEWAY;
  }
  if (httpStatus === HTTP_STATUS.CONFLICT) {
    return REPORT_CREATE_ERROR_KINDS.DUPLICATE;
  }

  const hasNoSourceCode = apiErrorKey === NO_SOURCE_ERROR_CODE;
  const hasEntitlementCode = apiErrorKey !== undefined && ENTITLEMENT_ERROR_CODES.has(apiErrorKey);
  // Legacy endpoints reject a request with a 400, Manage API endpoints with a 422
  const isRejectedRequest =
    httpStatus === HTTP_STATUS.VALIDATION_ERROR || httpStatus === HTTP_STATUS.UNPROCESSABLE_ENTITY;

  if (hasNoSourceCode) {
    return REPORT_CREATE_ERROR_KINDS.NO_SOURCE;
  }
  if (hasEntitlementCode) {
    return REPORT_CREATE_ERROR_KINDS.ENTITLEMENT;
  }
  if (!isRejectedRequest) {
    return REPORT_CREATE_ERROR_KINDS.UNKNOWN;
  }

  const isEntitlement = apiErrorKey !== undefined && ENTITLEMENT_BODY_KEYS.has(apiErrorKey);
  const hasDuplicateKey = apiErrorKey !== undefined && DUPLICATE_BODY_KEYS.has(apiErrorKey);
  const hasDuplicateMessage = apiErrorDetail !== undefined && DUPLICATE_MESSAGE.test(apiErrorDetail);

  if (isEntitlement) {
    return REPORT_CREATE_ERROR_KINDS.ENTITLEMENT;
  }
  if (hasDuplicateKey || hasDuplicateMessage) {
    return REPORT_CREATE_ERROR_KINDS.DUPLICATE;
  }

  return REPORT_CREATE_ERROR_KINDS.VALIDATION;
};

/**
 * Reads the HTTP status, the first API error key and the failure kind out of a report-creation
 * error. Accepts the `ReportCreateError` thrown by the mutations as well as a bare `ApiError`.
 */
export const classifyReportCreateError = (error: unknown) => {
  const reportError = isReportCreateError(error) ? error : null;
  const cause = reportError ? reportError.cause : error;
  const apiError = isApiError(cause) ? cause : null;
  const httpStatus = apiError?.status;
  const bodyEntry = apiError && !isNetworkError(apiError) ? getBodyEntry(apiError.error) : null;
  const apiErrorKey = reportError?.reason ?? bodyEntry?.key;
  const apiErrorDetail = bodyEntry ? describeValue(bodyEntry.value) : undefined;

  return {
    httpStatus,
    apiErrorKey,
    apiErrorDetail,
    kind: resolveKind({ httpStatus, apiErrorKey, apiErrorDetail, isAppDetected: reportError?.reason !== undefined })
  };
};

/**
 * Short GA-friendly description of a report-creation or run error, e.g. `400 noCredits` or
 * `422 REPORT_RUN_QUOTA_EXCEEDED`. Falls back to the underlying error message when the failure
 * carries no status or key.
 */
export const describeReportCreateError = (error: unknown) => {
  const cause = isReportCreateError(error) ? error.cause : error;

  // A request that never got a response carries the factory's default 500 status,
  // which would read as a backend failure in GA.
  if (isNetworkApiError(cause)) {
    return 'network';
  }

  const { httpStatus, apiErrorKey, kind } = classifyReportCreateError(error);
  const hasClassification = httpStatus !== undefined || apiErrorKey !== undefined;

  if (!hasClassification) {
    return cause instanceof Error ? cause.message : 'Unknown error';
  }

  return [httpStatus, apiErrorKey ?? kind].filter((part) => part !== undefined).join(' ');
};
